import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
      if (navigator.onLine) {
        toast.success('Back online! All features are now available.');
      } else {
        toast.error('You are offline. Some features may be limited.');
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        toast.success('Thanks for installing QRForge!');
      }
      
      setDeferredPrompt(null);
    } catch (err) {
      toast.error('Error installing the app');
      console.error('Error installing PWA:', err);
    }
  };

  if (!deferredPrompt) return null;

  return (
    <aside
      role="dialog"
      aria-labelledby="install-title"
      className="fixed inset-x-3 bottom-3 z-50 max-w-sm border-2 bg-card p-4 shadow-[5px_5px_0_0_var(--foreground)] sm:right-4 sm:left-auto"
    >
      <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Install app</p>
      <h3 id="install-title" className="mb-2 text-xl font-black uppercase tracking-tight">QR Forge, offline</h3>
      <p className="mb-4 text-sm text-muted-foreground">
        Install QRForge on your device for quick access and offline use.
      </p>
      <div className="flex gap-2">
        <Button onClick={handleInstallClick} className="flex-1">
          Install
        </Button>
        <Button variant="outline" onClick={() => setDeferredPrompt(null)}>
          Maybe Later
        </Button>
      </div>
    </aside>
  );
}
